import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  Search,
  Users,
  AlertCircle,
  Upload,
  UserPlus,
  Download,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { useCurrentScope } from '../../hooks/useMobilisations';
import { usePeopleList, type PersonApi } from '../../hooks/usePeople';
import { usePeopleMemberships } from '../../hooks/usePeopleMemberships';
import { Button } from '../../components/ui/Button';
import { Pager } from '../../components/ui/Pager';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { PeopleTable } from '../../components/admin/PeopleTable';
import { PersonInfoModal } from '../../components/person/PersonInfoModal';
import { PersonNotesDialog } from '../../components/person/PersonNotesDialog';
import { PersonTasksModal } from '../../components/person/PersonTasksModal';
import { PersonPreflightModal } from '../../components/person/PersonPreflightModal';

const ALLOWED = ['admin', 'super_admin', 'coordinator', 'hr'];

const PAGE_SIZE = 25;

/**
 * Admin browser for the people table. Search by name/email, click
 * through to /admin/people/:id for the edit page. Visual brief:
 * matches the dashboard tile pattern with purple admin accent.
 *
 * Pagination is client-side: the list endpoint returns the full
 * (capped) result set and we slice it here. Fine at mobilisation
 * scale; revisit with server-side paging if the table ever grows
 * past the API's row cap.
 */
export function PeopleAdmin() {
  const { data: user, isLoading } = useCurrentUser();
  // Day-to-day onboarding has no "attendees"; it has employees.
  const scope = useCurrentScope();
  const addNoun = scope?.kind === 'onboarding' ? 'employee' : 'attendee';
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [unallocatedOnly, setUnallocatedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const list = usePeopleList(q, undefined, undefined, showArchived);
  const memberships = usePeopleMemberships();
  const [peeking, setPeeking] = useState<PersonApi | null>(null);
  const [notesPerson, setNotesPerson] = useState<PersonApi | null>(null);
  const [tasksPerson, setTasksPerson] = useState<PersonApi | null>(null);
  const [preflightPerson, setPreflightPerson] = useState<PersonApi | null>(null);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        People admin is restricted to admin / coordinator / HR.
      </main>
    );
  }

  const filtered = (list.data ?? []).filter(
    (p) => !unallocatedOnly || (memberships.data?.[p.id] ?? []).length === 0,
  );
  // Clamp rather than reset in an effect: a shrinking result set (e.g.
  // narrowing the search) lands on the last valid page with no flicker.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Database className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-accent">
                Admin
              </div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                People
              </h1>
              <p className="mt-1 text-sm text-fg-muted">
                Browse and edit attendee records: notes, pre-employment
                checklist, GDPR exports.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Full-roster download for offline cross-referencing. Plain
                anchor to the GET endpoint (same pattern as the reports
                exports); the browser handles the file save. */}
            <a href="/api/people/export.xlsx" download>
              <Button
                variant="secondary"
                size="md"
                iconLeft={<Download className="h-4 w-4" />}
              >
                Export to Excel
              </Button>
            </a>
            <Link to="/admin/import-attendees">
              <Button
                variant="secondary"
                size="md"
                iconLeft={<Upload className="h-4 w-4" />}
              >
                Import CSV
              </Button>
            </Link>
            <Link to="/admin/people/new">
              <Button
                variant="primary"
                size="md"
                iconLeft={<UserPlus className="h-4 w-4" />}
              >
                Add {addNoun}
              </Button>
            </Link>
          </div>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-dim" />
            <input
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name or email..."
              className="block h-12 w-full rounded-lg border border-line-strong/20 bg-surface pl-11 pr-3 text-base shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </div>
          <label className="flex cursor-pointer select-none items-center gap-2 whitespace-nowrap text-sm text-fg-muted">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-line-strong/30 accent-accent"
            />
            Show archived
          </label>
          <label
            className="flex cursor-pointer select-none items-center gap-2 whitespace-nowrap text-sm text-fg-muted"
            title="People in no mobilisation day are invisible to reception and station staff"
          >
            <input
              type="checkbox"
              checked={unallocatedOnly}
              onChange={(e) => {
                setUnallocatedOnly(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-line-strong/30 accent-accent"
            />
            Not in any mobilisation
          </label>
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {list.isLoading ? (
            <SkeletonList rows={6} />
          ) : list.error ? (
            <EmptyState
              icon={<AlertCircle className="h-6 w-6" />}
              title="Couldn't load people"
              description={(list.error as Error).message}
            />
          ) : !list.data || list.data.length === 0 ? (
            q ? (
              <EmptyState
                icon={<Search className="h-6 w-6" />}
                title="No matches"
                description={`Nothing for "${q}". Check spelling or clear the search.`}
              />
            ) : (
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title="No people yet"
                description={`Add a single ${addNoun} or bulk-import a CSV to get started.`}
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    <Link to="/admin/people/new">
                      <Button variant="primary" iconLeft={<UserPlus className="h-4 w-4" />}>
                        Add {addNoun}
                      </Button>
                    </Link>
                    <Link to="/admin/import-attendees">
                      <Button variant="secondary" iconLeft={<Upload className="h-4 w-4" />}>
                        Import CSV
                      </Button>
                    </Link>
                  </div>
                }
              />
            )
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="Everyone is allocated"
              description="No people are outside a mobilisation day. Untick the filter to see the full list."
            />
          ) : (
            <PeopleTable
              people={pageRows}
              memberships={memberships.data}
              membershipsLoading={memberships.isLoading}
              onNotes={setNotesPerson}
              onTasks={setTasksPerson}
              onPreflight={setPreflightPerson}
              onInfo={setPeeking}
            />
          )}
        </div>
        <Pager
          page={safePage}
          limit={PAGE_SIZE}
          total={filtered.length}
          onPage={setPage}
          noun="people"
        />
      </div>
      {peeking ? (
        <PersonInfoModal person={peeking} onClose={() => setPeeking(null)} />
      ) : null}
      <PersonNotesDialog
        open={!!notesPerson}
        personId={notesPerson?.id ?? ''}
        personName={notesPerson?.full_name ?? ''}
        onClose={() => setNotesPerson(null)}
      />
      <PersonTasksModal
        open={!!tasksPerson}
        personId={tasksPerson?.id ?? ''}
        personName={tasksPerson?.full_name ?? ''}
        onClose={() => setTasksPerson(null)}
      />
      <PersonPreflightModal
        open={!!preflightPerson}
        personId={preflightPerson?.id ?? ''}
        personName={preflightPerson?.full_name ?? ''}
        onClose={() => setPreflightPerson(null)}
      />
    </main>
  );
}
